import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { PageTitle } from '../components/Layouts.js';
import {
  AI,
  EffortBars,
  EffortBenefitMatrix,
  Legend,
  useFormat,
  hi,
  lo,
  useModernization,
  type Modernization as Data,
} from '../components/modernization.js';
import { Button, Card, LoadError, Loading, Notice, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';

type Status = 'good' | 'warn' | 'bad' | 'unknown';
const CHIP: Record<Status, string> = {
  good: 'bg-green-100 text-green-900',
  warn: 'bg-amber-100 text-amber-900',
  bad: 'bg-red-100 text-red-900',
  unknown: 'bg-slate-100 text-slate-800',
};

function Chip({ status, children }: { status: Status; children: string }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-0.5 text-sm font-semibold tabular-nums ${CHIP[status]}`}
    >
      {children}
    </span>
  );
}

/**
 * Modernization (T-39, owner's remarks 1.1–1.5): what to improve, effort classic vs. with AI, cost
 * and – with the customer's figures – when it pays off.
 */
export function Modernization() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const query = useModernization(siteId);
  const f = useFormat();

  if (query.isPending) return <Loading />;
  if (query.isError)
    return (
      <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
    );
  const m = query.data;
  const maxHours = Math.max(1, ...m.packages.map((p) => hi(p.hours.classic)));

  return (
    <>
      <PageTitle
        actions={
          m.aiSaving ? (
            <span className="flex flex-col items-end">
              <span className="text-sm text-slate-700">{t('modern.aiSavingLabel')}</span>
              <span className="text-3xl font-bold tabular-nums" style={{ color: AI }}>
                −{f.percent(lo(m.aiSaving))}…{f.percent(hi(m.aiSaving))}
              </span>
            </span>
          ) : null
        }
      >
        {t('modern.title')}
      </PageTitle>
      <p className="-mt-4 mb-6 max-w-prose text-slate-700">{t('modern.intro')}</p>

      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <Facts m={m} />
        <Figures siteId={siteId} m={m} />
      </div>

      {m.packages.length === 0 ? (
        <Notice kind="success">{t('modern.nothing')}</Notice>
      ) : (
        <>
          <div className="mb-6 grid gap-4 lg:grid-cols-5">
            <Card className="lg:col-span-3">
              <h2 className="mb-1 text-xl font-semibold">{t('modern.matrix.title')}</h2>
              <p className="mb-2 text-sm text-slate-700">{t('modern.matrix.hint')}</p>
              <EffortBenefitMatrix packages={m.packages} />
            </Card>
            <Card className="flex flex-col gap-3 lg:col-span-2">
              <h2 className="text-xl font-semibold">{t('modern.how.title')}</h2>
              <Legend />
              <p className="text-sm leading-relaxed">{t('modern.how.classic')}</p>
              <p className="text-sm leading-relaxed">{t('modern.how.ai')}</p>
              <p className="text-sm leading-relaxed">{t('modern.how.benefit')}</p>
              <p className="rounded-md bg-slate-100 p-3 text-sm text-slate-800">
                {t('modern.disclaimer')}
              </p>
            </Card>
          </div>

          <section aria-labelledby="packages-title" className="mb-6">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 id="packages-title" className="text-2xl font-semibold">
                {t('modern.packagesTitle')}
              </h2>
              {m.packages.length > 1 ? (
                <Link
                  to={`/sites/${siteId}/modernization/compare`}
                  className="font-semibold text-blue-800 underline"
                >
                  {t('modern.compareLink')}
                </Link>
              ) : null}
            </div>
            <ul className="flex flex-col gap-3">
              {m.packages.map((p) => (
                <li key={p.key}>
                  <Card
                    className={`grid gap-4 md:grid-cols-[2fr_1.6fr_1fr_1fr_auto] md:items-center ${p.recommended ? 'border-green-300' : ''}`}
                  >
                    <div className="flex flex-col gap-1">
                      <h3 className="flex flex-wrap items-center gap-2 text-lg font-semibold">
                        {t(`modern.packages.${p.key}.title`)}
                        {p.recommended ? (
                          <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-sm text-green-900">
                            {t('modern.recommended')}
                          </span>
                        ) : null}
                      </h3>
                      <p className="text-sm text-slate-700">
                        {t(`modern.packages.${p.key}.summary`)}
                      </p>
                    </div>
                    <EffortBars hours={p.hours} max={maxHours} />
                    <div className="flex flex-col">
                      <span className="text-sm text-slate-700">{t('modern.costAi')}</span>
                      <b className="tabular-nums">{f.euroRange(p.cost.ai)}</b>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-slate-700">{t('modern.payback.label')}</span>
                      <b className="text-green-800">
                        {p.paybackMonths
                          ? f.months(p.paybackMonths)
                          : p.uplift
                            ? t('modern.payback.needFigures')
                            : t(`modern.packages.${p.key}.paybackNote`)}
                      </b>
                    </div>
                    <Link
                      to={`/sites/${siteId}/modernization/${p.key}`}
                      className={`inline-flex min-h-11 items-center justify-center rounded-md px-4 font-semibold no-underline ${p.recommended ? 'bg-blue-700 text-white' : 'border border-slate-300 text-slate-900'}`}
                    >
                      {t('modern.details')}
                    </Link>
                  </Card>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      <Extras m={m} />
    </>
  );
}

function Facts({ m }: { m: Data }) {
  const { t } = useTranslation();
  const f = useFormat();
  const x = m.facts;
  const rows: [string, string, Status][] = [];
  if (x.score !== null)
    rows.push([
      t('modern.facts.score'),
      `${x.score} / 100`,
      x.score >= 90 ? 'good' : x.score >= 50 ? 'warn' : 'bad',
    ]);
  if (x.lcpMs !== null)
    rows.push([
      t('modern.facts.lcp'),
      f.seconds(x.lcpMs),
      x.lcpMs <= 2500 ? 'good' : x.lcpMs <= 4000 ? 'warn' : 'bad',
    ]);
  if (x.pageBytes !== null)
    rows.push([
      t('modern.facts.weight'),
      f.megabytes(x.pageBytes),
      x.pageBytes <= 2_000_000 ? 'good' : x.pageBytes <= 5_000_000 ? 'warn' : 'bad',
    ]);
  if (x.unusedJsMs !== null && x.unusedJsMs >= 300)
    rows.push([t('modern.facts.unusedJs'), f.seconds(x.unusedJsMs), 'warn']);
  if (x.ttfbMs !== null)
    rows.push([
      t('modern.facts.ttfb'),
      t('modern.ms', { value: x.ttfbMs }),
      x.ttfbMs <= 800 ? 'good' : 'warn',
    ]);
  if (x.co2Grams !== null)
    rows.push([
      t('modern.facts.co2'),
      t('modern.co2', { value: f.num(x.co2Grams), median: f.num(x.co2MedianGrams) }),
      x.co2Grams <= x.co2MedianGrams ? 'good' : 'warn',
    ]);
  const problems = [...new Map(m.packages.flatMap((p) => p.findings).map((i) => [i.ruleCode, i]))];
  return (
    <Card className="flex flex-col gap-3 lg:col-span-2">
      <h2 className="text-xl font-semibold">{t('modern.facts.title')}</h2>
      {rows.length === 0 ? (
        <p className="text-slate-700">{t('modern.facts.notMeasured')}</p>
      ) : (
        <dl className="grid gap-x-8 sm:grid-cols-2">
          {rows.map(([label, value, status]) => (
            <div
              key={label}
              className="flex items-center justify-between gap-3 border-b border-slate-100 py-2"
            >
              <dt>{label}</dt>
              <dd>
                <Chip status={status}>{value}</Chip>
              </dd>
            </div>
          ))}
        </dl>
      )}
      {problems.length > 0 ? (
        <ul className="list-disc pl-5 text-sm text-slate-700">
          {problems.map(([code, p]) => (
            <li key={code}>{p.title}</li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

const toNumber = (v: string) => {
  const n = Number(v.replace(/\s/g, '').replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
};

function Figures({ siteId, m }: { siteId: string; m: Data }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const client = useQueryClient();
  const show = (v: number | null) =>
    v === null ? '' : String(v).replace('.', i18n.language === 'en' ? '.' : ',');
  const [form, setForm] = useState({
    rate: show(m.figures.hourlyRate),
    visitors: show(m.figures.monthlyVisitors),
    conversion: show(m.figures.conversionPct),
    order: show(m.figures.avgOrderEur),
  });
  useEffect(() => {
    setForm({
      rate: show(m.figures.hourlyRate),
      visitors: show(m.figures.monthlyVisitors),
      conversion: show(m.figures.conversionPct),
      order: show(m.figures.avgOrderEur),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.figures]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT('/sites/{siteId}/business-figures', {
          params: { path: { siteId } },
          body: {
            hourlyRate: toNumber(form.rate) ?? 85,
            monthlyVisitors: toNumber(form.visitors),
            conversionPct: toNumber(form.conversion),
            avgOrderEur: toNumber(form.order),
          },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId, 'modernization'] }),
  });
  return (
    <Card className="flex flex-col gap-3">
      <h2 className="text-xl font-semibold">{t('modern.figures.title')}</h2>
      <p className="text-sm text-slate-700">{t('modern.figures.hint')}</p>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <TextField
          label={t('modern.figures.rate')}
          inputMode="decimal"
          value={form.rate}
          onChange={(e) => setForm({ ...form, rate: e.target.value })}
        />
        <TextField
          label={t('modern.figures.visitors')}
          inputMode="numeric"
          value={form.visitors}
          onChange={(e) => setForm({ ...form, visitors: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label={t('modern.figures.conversion')}
            inputMode="decimal"
            value={form.conversion}
            onChange={(e) => setForm({ ...form, conversion: e.target.value })}
          />
          <TextField
            label={t('modern.figures.order')}
            inputMode="decimal"
            value={form.order}
            onChange={(e) => setForm({ ...form, order: e.target.value })}
          />
        </div>
        <Button type="submit" variant="secondary" busy={save.isPending}>
          {t('modern.figures.save')}
        </Button>
        {save.isError ? <p role="alert">{errorMessage(save.error, t)}</p> : null}
      </form>
      {m.monthlyRevenue !== null ? (
        <p className="text-sm">
          {t('modern.figures.revenue', { value: f.euro(m.monthlyRevenue) })}
        </p>
      ) : null}
    </Card>
  );
}

function Extras({ m }: { m: Data }) {
  const { t } = useTranslation();
  const f = useFormat();
  const co2 = m.facts.co2Grams;
  const cards: { key: string; status: Status; label: string; text: string }[] = [
    {
      key: 'bfsg',
      status: m.extras.bfsg === 'warn' ? 'warn' : 'unknown',
      label: t(m.extras.bfsg === 'warn' ? 'modern.extras.found' : 'modern.extras.notChecked'),
      text: t('modern.extras.bfsg'),
    },
    {
      key: 'local',
      status: m.extras.localBusiness === 'bad' ? 'bad' : 'unknown',
      label: t(
        m.extras.localBusiness === 'bad' ? 'modern.extras.missing' : 'modern.extras.notChecked',
      ),
      text: t('modern.extras.local'),
    },
  ];
  return (
    <section aria-labelledby="extras-title">
      <h2 id="extras-title" className="mb-3 text-2xl font-semibold">
        {t('modern.extras.title')}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <Card key={c.key} className="flex flex-col gap-2">
            <span className="self-start">
              <Chip status={c.status}>{c.label}</Chip>
            </span>
            <h3 className="font-semibold">{t(`modern.extras.${c.key}Title`)}</h3>
            <p className="text-sm text-slate-700">{c.text}</p>
          </Card>
        ))}
        {co2 !== null ? (
          <Card className="flex flex-col gap-2">
            <span className="self-start">
              <Chip status={co2 <= m.facts.co2MedianGrams ? 'good' : 'warn'}>
                {t('modern.gramsCo2', { value: f.num(co2) })}
              </Chip>
            </span>
            <h3 className="font-semibold">{t('modern.extras.co2Title')}</h3>
            <div
              className="flex flex-col gap-1"
              role="img"
              aria-label={t('modern.co2', {
                value: f.num(co2),
                median: f.num(m.facts.co2MedianGrams),
              })}
            >
              {(
                [
                  { value: co2, color: 'bg-red-600', label: f.num(co2) },
                  {
                    value: m.facts.co2MedianGrams,
                    color: 'bg-slate-400',
                    label: t('modern.extras.median', { value: f.num(m.facts.co2MedianGrams) }),
                  },
                ] as const
              ).map((bar) => (
                <div key={bar.color} className="flex items-center gap-2 text-xs">
                  <span className="h-3 flex-1 rounded bg-slate-100">
                    <span
                      className={`block h-3 rounded ${bar.color}`}
                      style={{
                        width: `${Math.max(3, (bar.value / Math.max(co2, m.facts.co2MedianGrams)) * 100)}%`,
                      }}
                    />
                  </span>
                  <span className="w-28 shrink-0">{bar.label}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-700">{t('modern.extras.co2')}</p>
          </Card>
        ) : null}
      </div>
    </section>
  );
}
